// ===================================
// ADMIN FEEDBACK PAGE
// src/pages/AdminFeedback.js
// Stage 5: Feedback management dashboard
// ===================================

import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './AdminFeedback.css';

const AdminFeedback = () => {
  // Data state
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    rating: '',
    source: '',
    wouldRecommend: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0
  });

  // Selected feedback for detail view
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Fetch feedback list
  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        limit: pagination.limit,
        offset: pagination.offset,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      // Add filters if set
      if (filters.rating) params.rating = filters.rating;
      if (filters.source) params.source = filters.source;
      if (filters.wouldRecommend !== '') params.wouldRecommend = filters.wouldRecommend;

      const response = await api.get('/feedback/admin/all', { params });
      setFeedback(response.data.feedback || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError(err.response?.data?.message || 'Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, pagination.offset]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await api.get('/feedback/admin/stats', { params: { days: 30 } });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchFeedback();
    fetchStats();
  }, [fetchFeedback, fetchStats]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
  };

  // Handle pagination
  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  // View feedback detail
  const viewFeedbackDetail = async (feedbackId) => {
    try {
      const response = await api.get(`/feedback/admin/${feedbackId}`);
      setSelectedFeedback(response.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error fetching feedback detail:', err);
      alert('Failed to load feedback details');
    }
  };

  // Delete feedback
  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;

    try {
      await api.delete(`/feedback/admin/${feedbackId}`);
      setFeedback(prev => prev.filter(f => f.id !== feedbackId));
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));
      if (showDetailModal) setShowDetailModal(false);
    } catch (err) {
      console.error('Error deleting feedback:', err);
      alert('Failed to delete feedback');
    }
  };

  // Render star rating
  const renderStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  // Get rating color class
  const getRatingColorClass = (rating) => {
    if (rating >= 4) return 'rating-high';
    if (rating >= 3) return 'rating-medium';
    return 'rating-low';
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render NPS indicator
  const renderNPS = (nps) => {
    let colorClass = 'nps-neutral';
    let label = 'Neutral';
    if (nps >= 50) { colorClass = 'nps-good'; label = 'Excellent'; }
    else if (nps >= 0) { colorClass = 'nps-ok'; label = 'Good'; }
    else { colorClass = 'nps-bad'; label = 'Needs Work'; }

    return (
      <div className={`nps-indicator ${colorClass}`}>
        <span className="nps-value">{nps}</span>
        <span className="nps-label">{label}</span>
      </div>
    );
  };

  // Calculate pages
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className="admin-feedback-page">
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">💬 User Feedback</h1>
          <p className="page-subtitle">View and manage feedback from interview sessions</p>
        </div>
        <button className="refresh-btn" onClick={() => { fetchFeedback(); fetchStats(); }}>
          🔄 Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="feedback-stats-grid">
        {statsLoading ? (
          <div className="stats-loading">Loading statistics...</div>
        ) : stats ? (
          <>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <div className="stat-value">{stats.overall?.total_feedback || 0}</div>
                <div className="stat-label">Total Feedback</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-content">
                <div className="stat-value">{stats.overall?.avg_rating || '0.0'}</div>
                <div className="stat-label">Avg Rating</div>
              </div>
            </div>

            <div className="stat-card nps-card">
              <div className="stat-icon">📈</div>
              <div className="stat-content">
                {renderNPS(stats.overall?.nps || 0)}
                <div className="stat-label">NPS Score</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">👍</div>
              <div className="stat-content">
                <div className="stat-value">
                  {stats.overall?.would_recommend_yes || 0}
                  <span className="stat-denominator">
                    / {(stats.overall?.would_recommend_yes || 0) + (stats.overall?.would_recommend_no || 0)}
                  </span>
                </div>
                <div className="stat-label">Would Recommend</div>
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Rating Distribution */}
      {stats && (
        <div className="rating-distribution-card">
          <h3>Rating Distribution</h3>
          <div className="rating-bars">
            {[5, 4, 3, 2, 1].map(rating => {
              const count = stats.overall?.[`${['one', 'two', 'three', 'four', 'five'][rating - 1]}_star`] || 0;
              const total = parseInt(stats.overall?.total_feedback) || 1;
              const percentage = ((count / total) * 100).toFixed(1);

              return (
                <div key={rating} className="rating-bar-row">
                  <span className="rating-label">{rating} ★</span>
                  <div className="rating-bar-container">
                    <div
                      className={`rating-bar rating-${rating}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="rating-count">{count} ({percentage}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label>Rating</label>
            <select
              value={filters.rating}
              onChange={(e) => handleFilterChange('rating', e.target.value)}
            >
              <option value="">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Source</label>
            <select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
            >
              <option value="">All Sources</option>
              <option value="email">Email</option>
              <option value="dashboard">Dashboard</option>
              <option value="app">App</option>
              <option value="api">API</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Would Recommend</label>
            <select
              value={filters.wouldRecommend}
              onChange={(e) => handleFilterChange('wouldRecommend', e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Sort By</label>
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <option value="created_at">Date</option>
              <option value="rating">Rating</option>
              <option value="overall_score">Session Score</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Order</label>
            <select
              value={filters.sortOrder}
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
            >
              <option value="DESC">Newest First</option>
              <option value="ASC">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={fetchFeedback}>Retry</button>
        </div>
      )}

      {/* Feedback Table */}
      <div className="feedback-table-container">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading feedback...</p>
          </div>
        ) : feedback.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No Feedback Found</h3>
            <p>No feedback matches your current filters.</p>
          </div>
        ) : (
          <table className="feedback-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Rating</th>
                <th>Interview Type</th>
                <th>Session Score</th>
                <th>Recommend</th>
                <th>Source</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {feedback.map(item => (
                <tr key={item.id}>
                  <td className="date-cell">{formatDate(item.created_at)}</td>
                  <td className="user-cell">
                    <div className="user-info">
                      <span className="user-name">{item.full_name || item.username || 'Unknown'}</span>
                      <span className="user-email">{item.email || '-'}</span>
                    </div>
                  </td>
                  <td className="rating-cell">
                    <span className={`rating-stars ${getRatingColorClass(item.rating)}`}>
                      {renderStars(item.rating)}
                    </span>
                  </td>
                  <td className="type-cell">
                    <span className={`type-badge type-${item.interview_type || 'text'}`}>
                      {item.interview_type === 'voice' ? '🎙️' : 
                       item.interview_type === 'phone' ? '📞' : '💬'}
                      {item.interview_type || 'text'}
                    </span>
                  </td>
                  <td className="score-cell">
                    {item.overall_score ? (
                      <span className={`score-badge ${
                        item.overall_score >= 80 ? 'score-high' :
                        item.overall_score >= 60 ? 'score-medium' : 'score-low'
                      }`}>
                        {item.overall_score}%
                      </span>
                    ) : '-'}
                  </td>
                  <td className="recommend-cell">
                    {item.would_recommend === true ? '✅ Yes' :
                     item.would_recommend === false ? '❌ No' : '-'}
                  </td>
                  <td className="source-cell">
                    <span className={`source-badge source-${item.source}`}>
                      {item.source || 'email'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button
                      className="action-btn view-btn"
                      onClick={() => viewFeedbackDetail(item.id)}
                      title="View Details"
                    >
                      👁️
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDeleteFeedback(item.id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && feedback.length > 0 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(0)}
          >
            « First
          </button>
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
          >
            ‹ Prev
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages} ({pagination.total} total)
          </span>
          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(pagination.offset + pagination.limit)}
          >
            Next ›
          </button>
          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange((totalPages - 1) * pagination.limit)}
          >
            Last »
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedFeedback && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content feedback-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Feedback Details</h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* User Info */}
              <div className="detail-section">
                <h3>👤 User Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Name</span>
                    <span className="detail-value">{selectedFeedback.full_name || selectedFeedback.username || 'Unknown'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{selectedFeedback.email || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div className="detail-section">
                <h3>⭐ Rating</h3>
                <div className="rating-display">
                  <span className={`big-rating ${getRatingColorClass(selectedFeedback.rating)}`}>
                    {renderStars(selectedFeedback.rating)}
                  </span>
                  <span className="rating-number">{selectedFeedback.rating}/5</span>
                </div>
                {selectedFeedback.would_recommend !== null && (
                  <div className="recommend-display">
                    Would Recommend: {selectedFeedback.would_recommend ? '✅ Yes' : '❌ No'}
                  </div>
                )}
              </div>

              {/* Session Info */}
              <div className="detail-section">
                <h3>📋 Session Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Interview Type</span>
                    <span className="detail-value">{selectedFeedback.interview_type || 'text'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Category</span>
                    <span className="detail-value">{selectedFeedback.category_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Session Score</span>
                    <span className="detail-value">{selectedFeedback.overall_score ? `${selectedFeedback.overall_score}%` : '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Duration</span>
                    <span className="detail-value">{selectedFeedback.duration ? `${selectedFeedback.duration} min` : '-'}</span>
                  </div>
                </div>
              </div>

              {/* Written Feedback */}
              {(selectedFeedback.what_went_well || selectedFeedback.what_to_improve || selectedFeedback.comments) && (
                <div className="detail-section">
                  <h3>📝 Written Feedback</h3>
                  {selectedFeedback.what_went_well && (
                    <div className="feedback-text-block">
                      <span className="feedback-text-label">What went well:</span>
                      <p>{selectedFeedback.what_went_well}</p>
                    </div>
                  )}
                  {selectedFeedback.what_to_improve && (
                    <div className="feedback-text-block">
                      <span className="feedback-text-label">What could improve:</span>
                      <p>{selectedFeedback.what_to_improve}</p>
                    </div>
                  )}
                  {selectedFeedback.comments && (
                    <div className="feedback-text-block">
                      <span className="feedback-text-label">Additional comments:</span>
                      <p>{selectedFeedback.comments}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Experience Areas */}
              {selectedFeedback.experience_areas && Object.keys(selectedFeedback.experience_areas).length > 0 && (
                <div className="detail-section">
                  <h3>📊 Experience Ratings</h3>
                  <div className="experience-areas">
                    {Object.entries(selectedFeedback.experience_areas).map(([key, value]) => (
                      <div key={key} className="experience-item">
                        <span className="experience-label">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span className="experience-value">{renderStars(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="detail-section metadata-section">
                <h3>ℹ️ Metadata</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Source</span>
                    <span className="detail-value">{selectedFeedback.source || 'email'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Submitted</span>
                    <span className="detail-value">{formatDate(selectedFeedback.created_at)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Session ID</span>
                    <span className="detail-value small-text">{selectedFeedback.session_id || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-danger"
                onClick={() => handleDeleteFeedback(selectedFeedback.id)}
              >
                🗑️ Delete Feedback
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowDetailModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFeedback;